import type { AiSuggestionInteractionPoint } from '@atlaskit/editor-common/analytics';
import { AiSuggestionsRegenerationTriggerValue } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { scrollRemixIntoView } from '../pm-plugins/remixScroll';
import { thinkingPluginKey } from '../pm-plugins/thinking-plugin';
import {
	addDismissAnalyticsForSelectionChange,
	addViewSuggestionAnalytics,
} from '../ui/utils/analytics';
import { applySuggestionDiff } from '../ui/utils/applySuggestionDiff';
import { createStaleSuggestionsUpdater } from '../ui/utils/staleSuggestionsUpdater';
import type { SelectSuggestionAction } from './types';

type SelectSuggestionOptions = Parameters<SelectSuggestionAction>[1];

/**
 * Show the diff for a suggestion in its own transaction (then scroll if there is
 * no diff). Used to refresh the diff after a stale suggestion is regenerated —
 * deliberately does NOT go through `selectSuggestion`, which would re-trigger
 * regeneration and loop.
 */
function showDiffForSuggestion({
	api,
	suggestion,
	options,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	options?: SelectSuggestionOptions;
	suggestion: Suggestion;
}): void {
	let noDiffScrollPos: number | undefined;
	api?.core?.actions.execute(({ tr }) => {
		noDiffScrollPos = applySuggestionDiff({ api, suggestion, tr, options });
		api?.userIntent?.commands.setCurrentUserIntent('reviewing')({ tr });
		return tr;
	});
	if (!scrollRemixIntoView(suggestion) && noDiffScrollPos !== undefined) {
		api?.core?.actions.scrollToPos(noDiffScrollPos, { behavior: 'smooth', block: 'start' });
	}
}

/**
 * On select, check the selected suggestion (or all suggestions when
 * `regenerateAll`) for staleness and regenerate any stale ones, retaining every
 * suggestion in place (each stale one is swapped for its regenerated counterpart,
 * which carries its new status + changes). No navigation or removal. Guarded so
 * only one regeneration batch runs at a time. The selected suggestion's diff is
 * refreshed (or hidden if it came back stale) once its data settles.
 */
async function regenerateOnSelect({
	api,
	selectedId,
	regenerateAll,
	options,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	options?: SelectSuggestionOptions;
	regenerateAll: boolean;
	selectedId: string;
}): Promise<void> {
	const state = api?.aiSuggestions?.sharedState.currentState();
	if (!state) {
		return;
	}
	// One regeneration batch at a time — rapid re-selects skip while one runs.
	if (state.regeneratingSuggestionIds.size > 0) {
		return;
	}

	const originalSelected = state.suggestionById.get(selectedId);
	const contentAri = originalSelected?.contentAri;
	if (!contentAri) {
		return;
	}

	// The suggestions to check: all, or just the selected one.
	const scope = (regenerateAll ? state.orderedSuggestionIds : [selectedId])
		.map((id) => state.suggestionById.get(id))
		.filter((suggestion): suggestion is Suggestion => suggestion !== undefined);

	// Read the current doc without dispatching (the command returns null).
	const getDoc = (): PMNode | undefined => {
		let doc: PMNode | undefined;
		api?.core?.actions.execute(({ tr }) => {
			doc = tr.doc;
			return null;
		});
		return doc;
	};

	const reconcile = createStaleSuggestionsUpdater(getDoc, contentAri, api, {
		regenerationTrigger: AiSuggestionsRegenerationTriggerValue.SUGGESTION_SELECTED,
		shouldRetainStale: true,
	});

	const updatedScope = await reconcile(scope);
	// Same reference back means nothing was regenerated (nothing stale / failed).
	if (updatedScope === scope) {
		return;
	}

	// Merge the (possibly scoped) updated suggestions back into the full list.
	const latest = api?.aiSuggestions?.sharedState.currentState();
	if (!latest) {
		return;
	}
	const updatedById = new Map(updatedScope.map((suggestion) => [suggestion.id, suggestion]));
	const mergedSuggestions = latest.orderedSuggestionIds
		.map((id) => updatedById.get(id) ?? latest.suggestionById.get(id))
		.filter((suggestion): suggestion is Suggestion => suggestion !== undefined);

	api?.core?.actions.execute(({ tr }) => {
		tr.setMeta(aiSuggestionsPluginKey, {
			action: 'SET_SUGGESTIONS',
			suggestions: mergedSuggestions,
			contentAri,
		});
		return tr;
	});

	// The selected suggestion was regenerated when its object identity changed.
	// Refresh its diff (fresh) or hide it (stale) — only while it's still selected.
	const afterMerge = api?.aiSuggestions?.sharedState.currentState();
	const updatedSelected = afterMerge?.suggestionById.get(selectedId);
	if (
		afterMerge?.selectedSuggestion === selectedId &&
		updatedSelected &&
		updatedSelected !== originalSelected
	) {
		if (updatedSelected.status === 'STALE') {
			api?.core?.actions.execute(({ tr }) => {
				api?.showDiff?.commands.hideDiff?.({ tr });
				return tr;
			});
		} else {
			showDiffForSuggestion({ api, suggestion: updatedSelected, options });
		}
	}
}

export const selectSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => SelectSuggestionAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) =>
	(
		id: string | null,
		options?: {
			hideDeletedDiffs?: boolean;
			interactionPoint?: AiSuggestionInteractionPoint;
			regenerateAll?: boolean;
			scrollIntoView?: boolean;
		},
	): boolean => {
		// Captured during the `execute` callback so we can run the scroll after the
		// transaction has been dispatched — that way `SELECT_SUGGESTION` has already
		// flowed through the plugin state and the card anchors are in the DOM by
		// the time the viewport moves into place.
		let noDiffScrollPos: number | undefined;

		// Read thinking status before execute to check if we should dismiss the
		// 'ready' bar — only when thinking is 'complete', not while still 'thinking'.
		const currentState = api?.aiSuggestions?.sharedState.currentState();
		const isSelectingNewSuggestion = currentState?.selectedSuggestion !== id;

		api?.core?.actions.execute(({ tr }) => {
			addDismissAnalyticsForSelectionChange({
				currentState,
				editorAnalyticsAPI: api?.analytics?.actions,
				interactionPoint: options?.interactionPoint,
				nextSuggestionId: id,
				transaction: tr,
			});

			tr.setMeta(aiSuggestionsPluginKey, { action: 'SELECT_SUGGESTION', id: id ?? undefined });

			tr.setMeta('addToHistory', false);

			// Close the 'ready' bar when a suggestion is opened,
			// but only if thinking is complete (don't dismiss while still thinking).
			if (id !== null && currentState?.thinkingStatus === 'complete') {
				tr.setMeta(thinkingPluginKey, { status: 'idle' });
			}

			if (id === null) {
				// Deselecting — hide the diff and clear the reviewing intent so node floating
				// toolbars become available again.
				api?.showDiff?.commands.hideDiff?.({ tr });
				api?.userIntent?.commands.setCurrentUserIntent('default')({ tr });
			} else {
				// Selecting — show the diff for this suggestion
				const suggestion = currentState?.suggestionById.get(id);
				if (suggestion) {
					if (options?.interactionPoint && isSelectingNewSuggestion) {
						addViewSuggestionAnalytics(api?.analytics?.actions)(
							tr,
							suggestion,
							options.interactionPoint,
						);
					}

					noDiffScrollPos = applySuggestionDiff({ api, suggestion, tr, options });
				}

				// Reviewing a suggestion card: suppress node floating toolbars so they don't overlap
				// or mis-position against the card / diff decorations. Set last so it wins over the
				// `viewingDiff`/`default` intent that showDiff/hideDiff write on this same transaction.
				api?.userIntent?.commands.setCurrentUserIntent('reviewing')({ tr });
			}

			return tr;
		});

		const selectedSuggestion = id === null ? undefined : currentState?.suggestionById.get(id);
		const scrolledRemix = selectedSuggestion ? scrollRemixIntoView(selectedSuggestion) : false;
		if (!scrolledRemix && noDiffScrollPos !== undefined) {
			api?.core?.actions.scrollToPos(noDiffScrollPos, { behavior: 'smooth', block: 'start' });
		}

		// Reconcile staleness in the background. Fresh → diff refreshed;
		// stale → kept and marked STALE for the card to render differently.
		if (id !== null) {
			void regenerateOnSelect({
				api,
				selectedId: id,
				regenerateAll: options?.regenerateAll ?? false,
				options,
			});
		}

		return true;
	};
